import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format, addMinutes } from 'date-fns';
import { customerService } from '../../services/customerService';
import { serviceService } from '../../services/serviceService';
import { staffService } from '../../services/staffService';

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
}

interface Service {
  id: string;
  name: string;
  duration: number;
  price: number;
  category: string;
}

interface Staff {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  skills: string[];
}

interface AppointmentFormData {
  customerId: string;
  staffId: string;
  services: string[];
  appointmentDate: string;
  startTime: string;
  notes: string;
}

const schema = yup.object({
  customerId: yup.string().required('Customer is required'),
  staffId: yup.string().required('Staff member is required'),
  services: yup.array().of(yup.string()).min(1, 'At least one service is required'),
  appointmentDate: yup.string().required('Date is required'),
  startTime: yup.string().required('Start time is required'),
  notes: yup.string(),
});

interface AppointmentFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  appointment?: any;
  loading?: boolean;
  preselectedDate?: Date;
  preselectedTime?: string;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  open,
  onClose,
  onSubmit,
  appointment,
  loading = false,
  preselectedDate,
  preselectedTime,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [totalDuration, setTotalDuration] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [endTime, setEndTime] = useState('');

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        setDataLoading(true);
        try {
          const [customersRes, servicesRes, staffRes] = await Promise.all([
            customerService.getCustomers({ page: 1, limit: 100 }),
            serviceService.getServices({ page: 1, limit: 100, isActive: true }),
            staffService.getStaff({ page: 1, limit: 100 }),
          ]);
          setCustomers((customersRes.customers || []).map((c) => ({
            id: c.id,
            firstName: c.firstName,
            lastName: c.lastName,
            phone: c.phone,
            email: c.email ?? undefined,
          })));
          const svcList = servicesRes.services || [];
          setServices(svcList.map((s: any) => ({
            id: s.id,
            name: s.name,
            duration: s.duration || 60,
            price: Number(s.price ?? 0),
            category: s.category || 'General',
          })));
          const staffList = staffRes.staff || staffRes;
          const staffArray = Array.isArray(staffList) ? staffList : [];
          setStaff(staffArray.map((s: any) => ({
            id: s.id,
            userId: s.userId || s.user?.id || s.id,
            firstName: s.firstName || s.user?.firstName,
            lastName: s.lastName || s.user?.lastName,
            skills: s.skills || [],
          })));
        } catch (err) {
          console.error('Failed to fetch form data:', err);
        } finally {
          setDataLoading(false);
        }
      };
      fetchData();
    }
  }, [open]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AppointmentFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      customerId: '',
      staffId: '',
      services: [],
      appointmentDate: preselectedDate ? format(preselectedDate, 'yyyy-MM-dd') : '',
      startTime: preselectedTime || '',
      notes: '',
    },
  });

  const watchedServices = watch('services');
  const watchedStartTime = watch('startTime');
  // const watchedDate = watch('appointmentDate');

  useEffect(() => {
    if (preselectedDate) {
      setValue('appointmentDate', format(preselectedDate, 'yyyy-MM-dd'));
    }
    if (preselectedTime) {
      setValue('startTime', preselectedTime);
    }
  }, [preselectedDate, preselectedTime, setValue]);

  useEffect(() => {
    // Update selected services and calculations
    const selected = services.filter(service => watchedServices.includes(service.id));
    setSelectedServices(selected);

    const duration = selected.reduce((sum, service) => sum + service.duration, 0);
    const amount = selected.reduce((sum, service) => sum + service.price, 0);

    setTotalDuration(duration);
    setTotalAmount(amount);

    // Calculate end time
    if (watchedStartTime && duration > 0) {
      const [hours, minutes] = watchedStartTime.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);
      const endDate = addMinutes(startDate, duration);
      setEndTime(format(endDate, 'HH:mm'));
    }
  }, [watchedServices, watchedStartTime, services]);

  useEffect(() => {
    if (appointment) {
      reset({
        customerId: appointment.customerId,
        staffId: appointment.staffId,
        services: (appointment.services || []).map((s: any) => s.serviceId),
        appointmentDate: format(new Date(appointment.appointmentDate), 'yyyy-MM-dd'),
        startTime: appointment.startTime,
        notes: appointment.notes || '',
      });
    } else {
      reset({
        customerId: '',
        staffId: '',
        services: [],
        appointmentDate: preselectedDate ? format(preselectedDate, 'yyyy-MM-dd') : '',
        startTime: preselectedTime || '',
        notes: '',
      });
    }
  }, [appointment, reset, preselectedDate, preselectedTime]);

  const handleFormSubmit = (data: AppointmentFormData) => {
    const appointmentData = {
      ...data,
      staffId: data.staffId,
      services: selectedServices.map(service => ({
        serviceId: service.id,
        serviceName: service.name,
        duration: service.duration,
        price: service.price,
      })),
      endTime,
      totalAmount,
      totalDuration,
    };
    onSubmit(appointmentData);
  };

  const getAvailableStaff = () => {
    if (selectedServices.length === 0) return staff;

    const requiredSkills = [...new Set(selectedServices.map(s => s.category))];
    return staff.filter(s =>
      requiredSkills.length === 0 || requiredSkills.every(skill => (s.skills || []).includes(skill))
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {appointment ? 'Edit Appointment' : 'Book New Appointment'}
      </DialogTitle>
      <DialogContent>
        {dataLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!dataLoading && (
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Customer Selection */}
            <Grid item xs={12} md={6}>
              <Controller
                name="customerId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={customers}
                    getOptionLabel={(option) =>
                      typeof option === 'string'
                        ? customers.find(c => c.id === option)?.firstName + ' ' + customers.find(c => c.id === option)?.lastName || ''
                        : `${option.firstName} ${option.lastName}`
                    }
                    value={customers.find(c => c.id === field.value) || null}
                    onChange={(_, value) => field.onChange(value?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        error={!!errors.customerId}
                        helperText={errors.customerId?.message}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body1">
                            {option.firstName} {option.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.phone}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                )}
              />
            </Grid>

            {/* Date and Time */}
            <Grid item xs={12} md={3}>
              <Controller
                name="appointmentDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.appointmentDate}
                    helperText={errors.appointmentDate?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Start Time"
                    type="time"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startTime}
                    helperText={errors.startTime?.message}
                  />
                )}
              />
            </Grid>

            {/* Services Selection */}
            <Grid item xs={12}>
              <Controller
                name="services"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.services}>
                    <InputLabel>Services</InputLabel>
                    <Select
                      {...field}
                      multiple
                      label="Services"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => {
                            const service = services.find(s => s.id === value);
                            return (
                              <Chip
                                key={value}
                                label={service?.name}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {services.map((service) => (
                        <MenuItem key={service.id} value={service.id}>
                          <Box>
                            <Typography variant="body1">{service.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {service.duration} min • ₹{service.price}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.services && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.services.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Staff Selection */}
            <Grid item xs={12} md={6}>
              <Controller
                name="staffId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.staffId}>
                    <InputLabel>Staff Member</InputLabel>
                    <Select {...field} label="Staff Member">
                      {getAvailableStaff().map((s) => (
                        <MenuItem key={s.id} value={s.userId}>
                          <Box>
                            <Typography variant="body1">
                              {s.firstName} {s.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Skills: {(s.skills || []).join(', ')}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.staffId && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.staffId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Appointment Summary */}
            {selectedServices.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Appointment Summary
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Duration:</Typography>
                      <Typography variant="body2">{totalDuration} minutes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">End Time:</Typography>
                      <Typography variant="body2">{endTime}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">Total Amount:</Typography>
                      <Typography variant="body2" fontWeight="bold">₹{totalAmount}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Notes */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    placeholder="Any special requirements or notes..."
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit(handleFormSubmit as any)}
          variant="contained"
          disabled={loading || dataLoading}
        >
          {loading ? 'Saving...' : appointment ? 'Update' : 'Book Appointment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentForm;